import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
let cache = null;
let promise = null;

const norm = (s) => (s || '').replace(/\s+/g, ' ').trim().toLowerCase();
export const csmStyleName = (raw) => (raw || '').replace(/\s+/g, ' ').trim()
  .toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()).replace('Api ', 'API ');

const EMPTY = { styles: [], drivers: [], locations: [], options: { products: [], drivers: [], pumpStyles: [] } };

// Full CSM reference catalogue (styles → manufacturers/fields/accessories, drivers, LSD layouts, option lists).
export default function useCsmCatalogue() {
  const [data, setData] = useState(cache);

  useEffect(() => {
    if (cache) return;
    promise = promise || axios.get(`${API}/csm-catalogue`).then((r) => { cache = r.data; return cache; }).catch(() => (cache = EMPTY));
    let alive = true;
    promise.then((d) => { if (alive) setData(d); });
    return () => { alive = false; };
  }, []);

  return useMemo(() => {
    const cat = data || EMPTY;
    const findStyle = (style) => cat.styles.find((s) => norm(s.name) === norm(style) || norm(csmStyleName(s.name)) === norm(style));
    const findMfr = (style, make) => findStyle(style)?.manufacturers.find((m) => norm(m.name) === norm(make));
    return {
      loaded: !!data,
      products: cat.options.products || [],
      driverTypes: cat.drivers.map((d) => d.name),
      driverFields: (type) => cat.drivers.find((d) => norm(d.name) === norm(type))?.fields || [],
      lsdLayout: (province) => {
        const p = norm(province);
        const name = p === 'british columbia' ? 'BC LSD' : (p === 'alberta' || p === 'saskatchewan') ? 'ALBERTA & SASK LSD' : null;
        return name ? cat.locations.find((l) => l.name === name) : null;
      },
      // Spec fields for a unit: style-level fields (e.g. MATERIAL CODE) + manufacturer fields except MODEL.
      specFields: (style, make) => [
        ...(findStyle(style)?.fields || []),
        ...((findMfr(style, make)?.fields || []).filter((f) => !['MODEL', 'SIZE'].includes(f.label))),
      ],
      seriesOptions: (style, make) => (findMfr(style, make)?.series || []).map((s) => s.name),
      accessoryTypes: (style) => (findStyle(style)?.accessories || []).map((a) => a.name),
      accessoryManufacturers: (style, type) => findStyle(style)?.accessories.find((a) => norm(a.name) === norm(type))?.manufacturers || [],
    };
  }, [data]);
}
